// Toolbar for the BC-23 rich-text composer. Bound to a Tiptap Editor and
// re-renders on editor transactions via useEditorState so button highlight
// state matches the current selection/marks.

import { Bold, Italic, Strikethrough, Code, Link as LinkIcon, List, ListOrdered, Quote, Image as ImageIcon } from 'lucide-react'
import { useEditorState, type Editor } from '@tiptap/react'
import { getMarkRange } from '@tiptap/core'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { LinkDialog } from './LinkDialog'

interface RichToolbarProps {
  editor: Editor | null
  onPickGif?: () => void
  /** Hide the block-level controls (lists, quote) for editors configured with
   *  RichTextEditor's inlineOnly — those commands have no extension to run. */
  inlineOnly?: boolean
}

export function RichToolbar({ editor, onPickGif, inlineOnly = false }: RichToolbarProps) {
  const [linkOpen, setLinkOpen] = useState(false)

  const state = useEditorState({
    editor,
    selector: ({ editor }) => editor ? {
      bold:       editor.isActive('bold'),
      italic:     editor.isActive('italic'),
      strike:     editor.isActive('strike'),
      code:       editor.isActive('code'),
      link:       editor.isActive('link'),
      bulletList: editor.isActive('bulletList'),
      orderedList:editor.isActive('orderedList'),
      blockquote: editor.isActive('blockquote')
    } : null,
    // Selector returns a fresh object every transaction. Without a shallow
    // equality check the toolbar re-renders on every cursor move; with one,
    // it only re-renders when one of the tracked mark/node states flips.
    equalityFn: shallowEqual
  })

  if (!editor || !state) return null

  const currentHref = (editor.getAttributes('link').href as string | undefined) ?? ''
  const currentText = computeLinkText(editor, state.link)

  function applyLink(text: string, url: string) {
    if (!editor) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    const display = text || url
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .insertContent({ type: 'text', text: display, marks: [{ type: 'link', attrs: { href: url } }] })
      .run()
  }

  const inline: { key: string; icon: typeof Bold; label: string; shortcut?: string; active: boolean; onClick: () => void }[] = [
    { key: 'bold',   icon: Bold,          label: 'Bold',          shortcut: '⌘B', active: state.bold,   onClick: () => editor.chain().focus().toggleBold().run() },
    { key: 'italic', icon: Italic,        label: 'Italic',        shortcut: '⌘I', active: state.italic, onClick: () => editor.chain().focus().toggleItalic().run() },
    { key: 'strike', icon: Strikethrough, label: 'Strikethrough',                 active: state.strike, onClick: () => editor.chain().focus().toggleStrike().run() },
    { key: 'code',   icon: Code,          label: 'Code',          shortcut: '⌘E', active: state.code,   onClick: () => editor.chain().focus().toggleCode().run() },
    { key: 'link',   icon: LinkIcon,      label: 'Link',          shortcut: '⌘K', active: state.link,   onClick: () => setLinkOpen(true) }
  ]

  const blocks = [
    { key: 'list',  icon: List,        label: 'Bullet list',   active: state.bulletList,  onClick: () => editor.chain().focus().toggleBulletList().run() },
    { key: 'ol',    icon: ListOrdered, label: 'Numbered list', active: state.orderedList, onClick: () => editor.chain().focus().toggleOrderedList().run() },
    { key: 'quote', icon: Quote,       label: 'Blockquote',    active: state.blockquote,  onClick: () => editor.chain().focus().toggleBlockquote().run() }
  ]

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-b border-border/60 bg-card/40">
      {inline.map(({ key, ...b }) => <ToolbarButton key={key} {...b} />)}
      {!inlineOnly && (
        <>
          <span className="w-px h-5 bg-border/60 mx-1" />
          {blocks.map(({ key, ...b }) => <ToolbarButton key={key} {...b} />)}
        </>
      )}
      {onPickGif && (
        <>
          <span className="w-px h-5 bg-border/60 mx-1" />
          <ToolbarButton icon={ImageIcon} label="Insert GIF" active={false} onClick={onPickGif} />
        </>
      )}
      <LinkDialog
        open={linkOpen}
        initialUrl={currentHref}
        initialText={currentText}
        onOpenChange={setLinkOpen}
        onSubmit={applyLink}
      />
    </div>
  )
}

// Pre-fill text for the link dialog. If the cursor is on an existing link,
// returns the whole link's label (not just the selected slice). Otherwise
// returns whatever text is currently selected.
function computeLinkText(editor: Editor, onLink: boolean): string {
  const { $from, from, to } = editor.state.selection
  if (onLink) {
    const range = getMarkRange($from, editor.schema.marks.link)
    if (range) return editor.state.doc.textBetween(range.from, range.to, ' ')
  }
  return editor.state.doc.textBetween(from, to, ' ')
}

interface ToolbarButtonProps {
  icon: typeof Bold
  label: string
  shortcut?: string
  active: boolean
  onClick: () => void
}

function shallowEqual<T extends Record<string, unknown> | null>(a: T, b: T): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) return false
  return aKeys.every(k => a[k] === b[k])
}

function ToolbarButton({ icon: Icon, label, shortcut, active, onClick }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick() }}
      aria-label={label}
      title={shortcut ? `${label} (${shortcut})` : label}
      aria-pressed={active}
      className={cn(
        'p-1.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer',
        active && 'bg-primary/15 text-primary hover:bg-primary/20'
      )}
    >
      <Icon className="size-4" />
    </button>
  )
}
